import React from 'react';
import ReactSwitch from 'react-switch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { LoiComparisonFormDto } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import { LevelOfService } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import {
    PriceRow,
    computeTotalValues,
    getFieldComment,
    formatBoolean,
    formatCell,
    buildCashAtCloseRow,
    buildDeferredFixedRow,
    buildDeferredVariableRow,
    buildEarnoutRow,
    buildSyntheticJvRow,
    buildJvEquityRow,
    buildCorporateEquityRow,
    buildOtherRow,
} from './LoiComparisonRows';
import { DEEP_DIVE_REGISTRY } from './DeepDiveDetailPanel/detail-config';
import { isLoiProcessCompleted } from '../../../../slices/marketplace/loi-comparison/models/loi-forms-process.model';

export const TAIL_COLUMN_HEADERS = [
    'Fees',
    'Net of Fees',
    'Employee Portion',
    'Net to Owner',
];

export const PRICE_ROWS: {
    label: string;
    getCells: (
        dto: LoiComparisonFormDto,
        totalSellerFees: number | null,
    ) => import('./LoiComparisonRows').RowCellValues;
    indented?: boolean;
}[] = [
    { label: 'Cash at Close', getCells: (dto, fees) => buildCashAtCloseRow(dto, fees) },
    { label: 'Deferred Fixed Payments', getCells: (dto) => buildDeferredFixedRow(dto) },
    { label: 'Deferred Variable Payments', getCells: (dto) => buildDeferredVariableRow(dto) },
    { label: 'Earnout', getCells: (dto) => buildEarnoutRow(dto), indented: true },
    { label: 'Synthetic JV Equity', getCells: (dto) => buildSyntheticJvRow(dto), indented: true },
    { label: 'JV Equity', getCells: (dto) => buildJvEquityRow(dto) },
    { label: 'Corporate Equity', getCells: (dto) => buildCorporateEquityRow(dto) },
    { label: 'Other', getCells: (dto) => buildOtherRow(dto) },
];

export const CONTRACT_LABELS = [
    { label: 'Lab Contract(s)', key: 'labContract' as const, explanationKey: 'labContractExplanation' as const },
    { label: 'Operating Leases', key: 'operatingLeases' as const, explanationKey: 'operatingLeasesExplanation' as const },
    { label: 'Capital Leases', key: 'capitalLeases' as const, explanationKey: 'capitalLeasesExplanation' as const },
    { label: 'Real-Estate Lease', key: 'realEstateLease' as const, explanationKey: 'realEstateLeaseExplanation' as const },
];

export const NON_COMP_LABELS = [
    { label: 'Distance: Radius (miles)', key: 'distanceRadiusMiles' as const },
    { label: 'Duration: Months from Closing', key: 'durationMonthsFromClosing' as const },
    { label: 'Duration: Months from Employment', key: 'durationMonthsFromEmployment' as const },
];

export const COLLAPSIBLE_COLUMN_TOKEN = '__pct:';

export interface BuyerSummaryCardProps {
    item: LoiComparisonFormDto;
    showFairValue: boolean;
    collapsibleColumns: Record<string, boolean>;
    onToggleColumn: (key: string) => void;
    contractExplanationReservedKeys: Set<string>;
    onBuyerClick?: () => void;
    isDeepDive: boolean;
    selectedRowLabel: string | null;
    onCellsClick: (rowLabel: string, buyerId: number) => void;
    isSeller: boolean;
    isEventCompleted: boolean;
    canSelectThisBuyer: boolean;
    onSelectClick: () => void;
    onResubmissionToggle: (buyerId: number) => void;
    onViewHistory?: () => void;
    tooltipIdSuffix?: string;
    isBroker?: boolean;
    onApproveClick?: () => void;
    onDeclineClick?: () => void;
}

export const BuyerSummaryCard = ({
    item,
    showFairValue,
    collapsibleColumns,
    onToggleColumn,
    contractExplanationReservedKeys,
    onBuyerClick,
    isDeepDive,
    selectedRowLabel,
    onCellsClick,
    isSeller,
    isEventCompleted,
    canSelectThisBuyer,
    onSelectClick,
    onResubmissionToggle,
    onViewHistory,
    tooltipIdSuffix = '',
    isBroker,
    onApproveClick,
    onDeclineClick,
}: BuyerSummaryCardProps) => {
    const completedProcesses = item.loiFormsProcesses.filter(isLoiProcessCompleted);
    const totalSellerFees = item.vvConnectFees?.netFeePaymentAtClosing?.fee ?? null;
    const totalCells = computeTotalValues(item.form?.termForm?.purchasePriceSummary, totalSellerFees);
    const totalFaceValue = totalCells.faceValue ?? 0;
    const totalFairValue = totalCells.fairValue ?? 0;

    const contracts = item.form?.termForm?.assumptionOfMajorContracts;
    const nonComp = item.form?.termForm?.nonCompetitionRestriction;

    const amcComments = CONTRACT_LABELS
        .map((_, i) => getFieldComment(item, `amc:${i}`))
        .filter(Boolean);
    const amcComment = amcComments.length > 0 ? amcComments.join('\n') : undefined;

    const ncrComments = NON_COMP_LABELS
        .map((_, i) => getFieldComment(item, `ncr:${i}`))
        .filter(Boolean);
    const ncrComment = ncrComments.length > 0 ? ncrComments.join('\n') : undefined;

    const columnHeaders = [
        'Face Values',
        `${COLLAPSIBLE_COLUMN_TOKEN}face`,
        ...(showFairValue ? ['Fair Value', `${COLLAPSIBLE_COLUMN_TOKEN}fair`] : []),
        ...TAIL_COLUMN_HEADERS,
    ];
    const colSpan = columnHeaders.length;

    return (
        <div className="buyer-summary-card">
            <table>
                <thead>
                    <tr>
                        <th colSpan={colSpan} className="buyer-group-header">
                            <div className="buyer-group-header__row">
                                {onBuyerClick ? (
                                    <span className="clickable-text" onClick={onBuyerClick}>
                                        {item.buyerName}
                                    </span>
                                ) : (
                                    <span>{item.buyerName}</span>
                                )}
                                {completedProcesses.length > 1 && onViewHistory && (
                                    <>
                                        <span className="buyer-group-header__separator">—</span>
                                        <button
                                            className="loi-view-history-btn"
                                            onClick={onViewHistory}>
                                            View History
                                        </button>
                                    </>
                                )}
                                {item.isSelected && (
                                    <span className="buyer-summary-card__selected-marker text-success">
                                        Selected
                                    </span>
                                )}
                                {item.isAwaitingAdvisorApproval && (
                                    <span className="buyer-summary-card__selected-marker text-warning">
                                        Waiting For Approval
                                    </span>
                                )}
                            </div>
                        </th>
                    </tr>
                    <tr className="sub-col-header-row">
                        {columnHeaders.map((label, idx) => {
                            if (label.startsWith(COLLAPSIBLE_COLUMN_TOKEN)) {
                                const pctKey = label.slice(COLLAPSIBLE_COLUMN_TOKEN.length);
                                const visible = collapsibleColumns[pctKey];
                                return visible ? (
                                    <th key={`${label}-${idx}`} className="sub-col-header">
                                        <button
                                            className="percent-toggle-btn"
                                            onClick={() => onToggleColumn(pctKey)}
                                            aria-label="Collapse % column"
                                        >
                                            <FontAwesomeIcon icon={faMinus} />
                                        </button>
                                        % of Total
                                    </th>
                                ) : (
                                    <th key={`${label}-${idx}`} className="percent-expander-col">
                                        <button
                                            className="percent-toggle-btn"
                                            onClick={() => onToggleColumn(pctKey)}
                                            aria-label="Expand % column"
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    </th>
                                );
                            }
                            return <th key={`${label}-${idx}`} className="sub-col-header">{label}</th>;
                        })}
                    </tr>
                </thead>
                <tbody>
                    {PRICE_ROWS.map(({ label, getCells, indented }) => {
                        const cells = getCells(item, totalSellerFees);
                        const isClickable = DEEP_DIVE_REGISTRY[label] !== undefined || label === 'Other';
                        const isExpanded = isDeepDive && selectedRowLabel === label;
                        return (
                            <PriceRow
                                key={label}
                                label={label}
                                indented={indented}
                                cells={cells}
                                totalFaceValue={totalFaceValue}
                                totalFairValue={totalFairValue}
                                showFairValue={showFairValue}
                                showFaceColumn={collapsibleColumns.face}
                                showFairColumn={collapsibleColumns.fair}
                                buyerId={item.buyerId}
                                comment={getFieldComment(item, cells.fieldKey)}
                                isDeepDive={isDeepDive}
                                isClickable={isClickable}
                                isExpanded={isExpanded}
                                onCellsClick={(buyerId) => onCellsClick(label, buyerId)}
                                tooltipIdSuffix={tooltipIdSuffix}
                            />
                        );
                    })}
                    <PriceRow
                        label="Total Value"
                        cells={totalCells}
                        totalFaceValue={totalFaceValue}
                        totalFairValue={totalFairValue}
                        showFairValue={showFairValue}
                        showFaceColumn={collapsibleColumns.face}
                        showFairColumn={collapsibleColumns.fair}
                        buyerId={item.buyerId}
                        isBold
                        tooltipIdSuffix={tooltipIdSuffix}
                    />
                    {item.isResubmissionToggleVisible && !isDeepDive && (
                        <tr className="resubmission-toggle-row">
                            <td colSpan={colSpan}>
                                <div className="resubmission-toggle-content">
                                    <span className="resubmission-toggle-label">Enable Resubmission of Offer</span>
                                    <ReactSwitch
                                        offColor="#d92550"
                                        checked={item.isResubmissionEnabled}
                                        onChange={item.canToggleResubmission && !item.isResubmissionEnabled
                                            ? () => onResubmissionToggle(item.buyerId)
                                            : () => {}}
                                        disabled={!item.canToggleResubmission || item.isResubmissionEnabled}
                                        height={22}
                                    />
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {!isDeepDive && (
                <>
                    <div className="section-header">
                        <span>Assumption of Major Contracts</span>
                        {amcComment && (
                            <InfoTooltip
                                text={amcComment}
                                idText={`amc-comment-${item.buyerId}${tooltipIdSuffix}`}
                            />
                        )}
                    </div>
                    <div className="compact-grid compact-grid--contracts">
                        {CONTRACT_LABELS.map(({ label }) => (
                            <span key={`h-${label}`} className="compact-grid__header">{label}</span>
                        ))}
                        {CONTRACT_LABELS.map(({ label, key, explanationKey }) => {
                            const explanation = contracts?.[explanationKey];
                            const shouldReserveExplanation = contractExplanationReservedKeys.has(explanationKey);
                            return (
                                <div key={`v-${label}`} className="compact-grid__value">
                                    <span>{formatBoolean(contracts?.[key])}</span>
                                    {shouldReserveExplanation && (
                                        <span className="compact-grid__explanation">
                                            {explanation ? explanation : ' '}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="section-header">
                        <span>Non-Competition Restrictions</span>
                        {ncrComment && (
                            <InfoTooltip
                                text={ncrComment}
                                idText={`ncr-comment-${item.buyerId}${tooltipIdSuffix}`}
                            />
                        )}
                    </div>
                    <div className="compact-grid compact-grid--non-comp">
                        {NON_COMP_LABELS.map(({ label }) => (
                            <span key={`h-${label}`} className="compact-grid__header">{label}</span>
                        ))}
                        {NON_COMP_LABELS.map(({ label, key }) => (
                            <span key={`v-${label}`} className="compact-grid__value">
                                {formatCell(nonComp?.[key])}
                            </span>
                        ))}
                    </div>
                    {canSelectThisBuyer && !item.isSelected && (
                        <div className="buyer-summary-card__action-row">
                            <button
                                type="button"
                                className="btn btn-primary buyer-summary-card__select-btn"
                                onClick={onSelectClick}
                            >
                                Select &amp; Execute LOI
                            </button>
                        </div>
                    )}
                    {isBroker && item.isAwaitingAdvisorApproval && (
                        <div className="buyer-summary-card__action-row">
                            <button
                                type="button"
                                className="btn btn-primary buyer-summary-card__select-btn"
                                onClick={onApproveClick}
                            >
                                Approve Selection
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-danger buyer-summary-card__select-btn"
                                onClick={onDeclineClick}
                            >
                                Decline Selection
                            </button>
                        </div>
                    )}
                </>
            )}

        </div>
    );
};
